// utils/dateUtils.js

/**
 * Format date for display in chat
 * @param {Date} date - Date object
 * @returns {string} - Formatted date string
 */
export const formatMessageDate = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Check if date is today
  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }
  
  // Check if date is yesterday
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  
  // Check if date is within the last 7 days
  const daysDiff = Math.floor((today - date) / (1000 * 60 * 60 * 24));
  if (daysDiff < 7) {
    // Show weekday for dates within last 7 days
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
  
  // Check if date is within the same year
  if (date.getFullYear() === today.getFullYear()) {
    // For dates within same year but older than 7 days
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', // Added day name
      month: 'short', 
      day: 'numeric'
    });
  }
  
  // For dates from previous years - show full date with day
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', // Added day name
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Get time string from timestamp
 * @param {Date} date - Date object
 * @returns {string} - Formatted time string
 */
export const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Group messages by date
 * @param {Array} messages - Array of message objects
 * @returns {Object} - Messages grouped by date
 */
export const groupMessagesByDate = (messages) => {
  const grouped = {};

  console.log("Messages: ", messages);
  
  messages.forEach((message) => {
    const date = message.created_at?.toDate ? message.created_at.toDate() : new Date();
    const dateKey = date.toDateString(); // Use date string as key
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        date: date,
        displayDate: formatMessageDate(date),
        messages: []
      };
    }
    
    grouped[dateKey].messages.push(message);
  });

  console.log("Grouped Messages: ", grouped);
  
  return grouped;
};

/**
 * Check if two messages should be grouped together (same sender within 5 minutes)
 * @param {Object} prevMessage - Previous message
 * @param {Object} currentMessage - Current message
 * @returns {boolean} - Whether messages should be grouped
 */
export const shouldGroupMessages = (prevMessage, currentMessage) => {
  if (!prevMessage || !currentMessage) return false;
  
  const prevDate = prevMessage.created_at?.toDate ? prevMessage.created_at.toDate() : new Date(prevMessage.created_at);
  const currentDate = currentMessage.created_at?.toDate ? currentMessage.created_at.toDate() : new Date(currentMessage.created_at);
  
  const timeDiff = Math.abs(currentDate - prevDate) / (1000 * 60); // Difference in minutes
  
  return (
    prevMessage.sender_id === currentMessage.sender_id &&
    timeDiff <= 5 // Group messages from same sender within 5 minutes
  );
};